import * as React from 'react';
import classnames from 'classnames';

import InputRadio from 'components/inputs/input-radio/InputRadio';
import SVGDynamicIcon from 'components/svg-dynamic-icon/SVGDynamicIcon';
import Tooltip from 'components/Tooltip';

import { __t, applyTranslations } from 'utils/language';
import { abbrNum, addThousandsSeparator } from 'utils/helper/common';
import { sanitizeGenericHtml } from 'utils/sanitize';

import './campaign-package.scss';

applyTranslations(require('./campaign-package.i18n.json'));

import type {
  CampaignPackageToDetails,
  CampaignPackageType
} from './types';

type Props = {
  types: Array<CampaignPackageType>,
  packageToDetailsMap: CampaignPackageToDetails,
  selected?: CampaignPackageType,
  disabled?: boolean,
  onSelect: (type: CampaignPackageType) => void
};

const MSG_KEY = 'campaign_package';
const BULLETS = ['first', 'second'];

const CampaignPackage: React.FC<Props> = ({
  types,
  packageToDetailsMap,
  selected = '',
  disabled,
  onSelect
}): JSX.Element => {
  const getBulletText = (
    type: CampaignPackageType,
    bullet: string
  ): string => {
    const text = __t(`${MSG_KEY}.packages.${type}.bullets.${bullet}`, {
      numWeeks: packageToDetailsMap[type].numWeeks,
      numOutfitsRange: packageToDetailsMap[type].numOutfitsRange,
      numOutfitClicks: abbrNum(packageToDetailsMap[type].numOutfitClicks),
      numOutfitViews: abbrNum(packageToDetailsMap[type].numOutfitViews)
    });

    return sanitizeGenericHtml(text);
  };

  const handleSelect = (
    type: CampaignPackageType
  ): void => {
    onSelect(type);
  };

  const renderRadioButton = (
    type: CampaignPackageType
  ): React.ReactNode => (
    <div className="campaign-package__radio">
      <InputRadio
        key={`campaign-package-radio--${type}`}
        id={`campaign-package-${type}`}
        name={`campaign-package-${type}`}
        label=""
        value={type}
        checked={type === selected}
        onChange={() => handleSelect(type)}
      />
    </div>
  );

  const renderPill = (
    type: CampaignPackageType
  ): React.ReactNode => (
    <div className="campaign-package__pill">
      {__t(`${MSG_KEY}.packages.${type}.designator`)}&nbsp;
      {__t(`${MSG_KEY}.campaign`)}
    </div>
  );

  const renderCost = (
    type: CampaignPackageType
  ): React.ReactNode => (
    <div className="campaign-package__cost">
      <div>
        {addThousandsSeparator(packageToDetailsMap[type].promotionBudget)}€
      </div>
      {__t(`${MSG_KEY}.promotion_budget`)}
    </div>
  );

  const renderBullets = (
    type: CampaignPackageType
  ): React.ReactNode => (
    <div className="campaign-package__bullets">
      {
        BULLETS.map(bullet => (
          <div
            key={`campaign-package-bullet--${type}-${bullet}`}
            className="campaign-package__bullet"
          >
            <div className="campaign-package__bullet-icon">
              <SVGDynamicIcon
                name="checkmark-custom-fg"
                width={10}
                height={10}
                customColor="primary"
              />
            </div>
            <div
              className="campaign-package__bullet-text"
              dangerouslySetInnerHTML={{ __html: getBulletText(type, bullet) }}
            />
          </div>
        ))
      }
    </div>
  );

  const renderPackageContents = (
    type: CampaignPackageType
  ): React.ReactNode => (
    <React.Fragment>
      {renderRadioButton(type)}
      {renderPill(type)}
      {renderCost(type)}
      {renderBullets(type)}
    </React.Fragment>
  );

  const renderPackage = (
    type: CampaignPackageType,
    index: number
  ): React.ReactNode => {
    const isDisabled = type !== selected && Boolean(disabled);

    const itemClasses = classnames('campaign-package__item', {
      'campaign-package__item--selected': type === selected,
      'campaign-package__item--no-interaction': type === selected && disabled,
      'campaign-package__item--disabled': isDisabled
    });

    const Contents = (
      <div
        key={`campaign-package--${type}`}
        className={itemClasses}
        onClick={() => handleSelect(type)}
      >
        {renderPackageContents(type)}
      </div>
    );

    if (isDisabled) {
      return (
        <Tooltip
          key={index}
          message={__t(`${MSG_KEY}.tooltips.disabled`)}
          width="short"
          classNameWrapper="campaign-package__tooltip"
        >
          {Contents}
        </Tooltip>
      );
    }

    return Contents;
  };

  return (
    <div className="campaign-package">
      {types.map(renderPackage)}
    </div>
  );
};

export default CampaignPackage;
